/**
 * 홈 배너 API (어드민)
 *
 * - GET    /api/admin/banners
 * - GET    /api/admin/banners/{id}
 * - POST   /api/admin/banners
 * - PUT    /api/admin/banners/{id}
 * - DELETE /api/admin/banners/{id}
 * - POST   /api/admin/banners/upload-image
 */
import { apiClient } from './client';

export type BannerTargetApp = 'BUSINESS' | 'GUARDIAN';

export type BannerLinkType = 'NONE' | 'INTERNAL' | 'EXTERNAL';

export interface Banner {
  id: number;
  targetApp: BannerTargetApp;
  title?: string | null;
  fileUrl: string;
  linkType: BannerLinkType;
  linkUrl?: string | null;
  sortOrder: number;
  isUse: boolean;
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  createdBy?: number | null;
  updatedBy?: number | null;
  // 위치기반 노출
  companySeq?: number | null;
  isLocationBased?: boolean;
  radiusKm?: number | null;
  // AI 오버레이(표시-시점 합성)
  isOverlay?: boolean;
  headline?: string | null;
  subline?: string | null;
  textZone?: string | null;
  pointColor?: string | null;
  fontKey?: string | null;
  // 위치 노출 표시(연결 매장)
  companyName?: string | null;
  companyRegion?: string | null;
  // 효과(누적 노출/클릭)
  impressions?: number | null;
  clicks?: number | null;
}

/** AI 배너 비동기 생성 job 상태 */
export interface AiBannerJobStatus {
  jobId: string;
  status: 'GATHERING' | 'ANALYZING' | 'COMPOSING' | 'DONE' | 'FAILED';
  progress: number;
  step: string;
  done: boolean;
  failed: boolean;
  error?: string | null;
  result?: AiBannerResult | null;
}

/** 배너 효과 리포트 */
export interface BannerStats {
  impressions: number;
  clicks: number;
  ctr: number;
  daily: { date: string; impressions: number; clicks: number }[];
}

/** AI 배너 분석 결과 — 매장 사진 자동 취합·분석(서버). FE가 오버레이로 미리보기/저장. */
export interface AiBannerResult {
  heroUrl?: string | null;
  mediaCount: number;
  headline?: string | null;
  subline?: string | null;
  textZone?: string | null;
  dominantColor?: string | null;
  mood?: string | null;
  fontKey?: string | null;
  fallback: boolean;
  /** 분석에 쓴 사진 소스: WORKPLACE(홍보) | ALBUM(사진첩) | TEXT(무사진·정보만) */
  source?: 'WORKPLACE' | 'ALBUM' | 'TEXT' | null;
  /** Gemini 실분석 여부(false=규칙기반 폴백) */
  aiAnalyzed?: boolean;
}

export interface BannerCreateInput {
  targetApp: BannerTargetApp;
  title?: string;
  fileUrl: string;
  linkType?: BannerLinkType;
  linkUrl?: string;
  sortOrder?: number;
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  isUse?: boolean;
  companySeq?: number | null;
  isLocationBased?: boolean;
  radiusKm?: number | null;
  isOverlay?: boolean;
  headline?: string | null;
  subline?: string | null;
  textZone?: string | null;
  pointColor?: string | null;
  fontKey?: string | null;
}

export interface BannerUpdateInput {
  targetApp?: BannerTargetApp;
  title?: string;
  fileUrl?: string;
  linkType?: BannerLinkType;
  linkUrl?: string;
  sortOrder?: number;
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  clearDisplayStart?: boolean;
  clearDisplayEnd?: boolean;
  isUse?: boolean;
  companySeq?: number | null;
  isLocationBased?: boolean;
  radiusKm?: number | null;
  isOverlay?: boolean;
  headline?: string | null;
  subline?: string | null;
  textZone?: string | null;
  pointColor?: string | null;
  fontKey?: string | null;
}

export interface GetBannersParams {
  page?: number;
  size?: number;
  targetApp?: BannerTargetApp | 'ALL';
}

export interface BannerPage {
  content: Banner[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface BannerImageUploadResult {
  url: string;
  width?: number | null;
  height?: number | null;
}

const BASE = '/admin/banners';

export const bannersApi = {
  getBanners: async (params: GetBannersParams = {}): Promise<BannerPage> => {
    const res = await apiClient.get<BannerPage>(BASE, {
      params: {
        page: params.page ?? 0,
        size: params.size ?? 10,
        targetApp:
          params.targetApp && params.targetApp !== 'ALL'
            ? params.targetApp
            : undefined,
      },
    });
    return res.data;
  },

  getBannerDetail: async (id: number): Promise<Banner> => {
    const res = await apiClient.get<Banner>(`${BASE}/${id}`);
    return res.data;
  },

  createBanner: async (input: BannerCreateInput): Promise<Banner> => {
    const res = await apiClient.post<Banner>(BASE, input);
    return res.data;
  },

  updateBanner: async (
    id: number,
    input: BannerUpdateInput,
  ): Promise<Banner> => {
    const res = await apiClient.put<Banner>(`${BASE}/${id}`, input);
    return res.data;
  },

  deleteBanner: async (id: number): Promise<void> => {
    await apiClient.delete<void>(`${BASE}/${id}`);
  },

  /** 배너 이미지 업로드 → 응답 url 을 fileUrl 로 사용. 권장 사이즈 750x352(2x). */
  uploadImage: async (file: File): Promise<BannerImageUploadResult> => {
    const res = await apiClient.upload<BannerImageUploadResult>(
      `${BASE}/upload-image`,
      file,
    );
    return res.data;
  },

  /**
   * [AI] 매장 배너 자동 생성 — 매장 사진을 자동 취합·분석해 히어로컷 + 문구를 반환.
   * FE가 결과를 오버레이(fileUrl=heroUrl + headline/subline)로 미리보기/저장한다.
   */
  aiGenerate: async (companySeq: number): Promise<AiBannerResult> => {
    const res = await apiClient.post<AiBannerResult>(
      `${BASE}/ai-generate`,
      undefined,
      { params: { companySeq } },
    );
    return res.data;
  },

  /** 비동기 생성 시작 → jobId. status 폴링으로 진척률 표시. */
  aiGenerateAsync: async (companySeq: number): Promise<AiBannerJobStatus> => {
    const res = await apiClient.post<AiBannerJobStatus>(
      `${BASE}/ai-generate/async`,
      undefined,
      { params: { companySeq } },
    );
    return res.data;
  },

  aiGenerateStatus: async (jobId: string): Promise<AiBannerJobStatus> => {
    const res = await apiClient.get<AiBannerJobStatus>(
      `${BASE}/ai-generate/status/${jobId}`,
    );
    return res.data;
  },

  /** 배너 효과 리포트 (from/to: yyyy-MM-dd, 미지정 시 최근 30일) */
  getBannerStats: async (
    id: number,
    from?: string,
    to?: string,
  ): Promise<BannerStats> => {
    const res = await apiClient.get<BannerStats>(`${BASE}/${id}/stats`, {
      params: { from, to },
    });
    return res.data;
  },
};
